/**
 * OAuth Context and Hook for WayFong Authentication.
 *
 * Layer 1 of authentication — validates the user is on an authorized
 * (wallyco.net / wayfong.com) domain. The access token lives in memory only;
 * the long-lived refresh credential is an HttpOnly cookie managed by wfauth,
 * so a page refresh is recovered silently via /auth/token/refresh.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  validateToken,
  refreshAccessToken,
  initiateOAuth,
  getStoredUser,
  storeUser,
  clearToken,
  setApiWfAuthToken,
  logout as oauthServiceLogout,
  OAuthUser,
} from '../services/oauthService';
import {
  OAUTH_ENABLED,
  HTE_ACCESS_ENFORCE,
  TOKEN_VALIDATION_INTERVAL_MS,
  TOKEN_VALIDATION_FALLBACK_MS,
  TOKEN_VALIDATION_LEAD_MS,
} from '../config/oauth';

// Floor on scheduler delay so an already-expired token still gets re-checked
// promptly, but the loop can never run away into a tight cycle.
const MIN_VALIDATION_DELAY_MS = 30 * 1000;

interface OAuthContextType {
  // Whether OAuth validation has completed (token is valid)
  isOAuthValidated: boolean;
  // Whether OAuth validation is in progress
  isOAuthLoading: boolean;
  // User info from OAuth (email, name, picture)
  oauthUser: OAuthUser | null;
  // Error message if OAuth validation failed
  oauthError: string | null;
  // WCO-2914: whether the signed-in user holds HTE access. True when the gate is dormant
  // or not enforcing; false only when the backend confirmed the user lacks the grant.
  hasHteAccess: boolean;
  // Manually trigger OAuth flow (redirect to auth service)
  startOAuth: () => void;
  // Clear OAuth state and redirect to auth service
  clearOAuthAndRestart: () => void;
  // Validate a token and update state. `expiresIn` (seconds) lets the
  // scheduler fire just before the token dies; omit if unknown.
  validateAndStoreToken: (token: string, expiresIn?: number) => Promise<boolean>;
  // Sign the user out: invalidates server-side tokens (access token + refresh
  // cookie) and clears all client-side OAuth state.
  logout: () => Promise<void>;
}

const OAuthContext = createContext<OAuthContextType | undefined>(undefined);

/**
 * Hook to access OAuth state and functions
 */
export const useOAuth = (): OAuthContextType => {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error('useOAuth must be used within an OAuthProvider');
  }
  return context;
};

interface OAuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages OAuth state
 */
export const OAuthProvider: React.FC<OAuthProviderProps> = ({ children }) => {
  const [isOAuthValidated, setIsOAuthValidated] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(true);
  const [oauthUser, setOauthUser] = useState<OAuthUser | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  // WCO-2914: default true so a dormant/non-enforcing gate never blocks anyone.
  const [hasHteAccess, setHasHteAccess] = useState(true);

  // Access token + absolute expiry (epoch ms) live in memory only.
  // Stored in refs so updates from background refreshes don't re-render the
  // tree and don't churn the scheduler effect's cleanup.
  const accessTokenRef = useRef<string | null>(null);
  const expiresAtRef = useRef<number | null>(null);

  // Track which token (refresh stamp) the scheduler last saw, so a successful
  // refresh inside runCheck can reschedule against the new expiry without
  // restarting the whole effect.
  const [tokenEpoch, setTokenEpoch] = useState(0);

  const recordToken = useCallback((token: string, expiresIn?: number) => {
    accessTokenRef.current = token;
    // WCO-2914: publish for the API client to attach as x-wayfong-token (gateway re-check).
    setApiWfAuthToken(token);
    expiresAtRef.current =
      typeof expiresIn === 'number' && expiresIn > 0
        ? Date.now() + expiresIn * 1000
        : null;
    setTokenEpoch((n) => n + 1);
  }, []);

  const dropToken = useCallback(() => {
    accessTokenRef.current = null;
    setApiWfAuthToken(null);
    expiresAtRef.current = null;
  }, []);

  /**
   * Validate a freshly-exchanged token and bring the provider online.
   * Used by OAuthCallback after /auth/token/exchange returns. We call validate
   * to fetch user info; the token itself is stored in memory only.
   */
  const validateAndStoreToken = useCallback(
    async (token: string, expiresIn?: number): Promise<boolean> => {
      setIsOAuthLoading(true);
      setOauthError(null);

      const result = await validateToken(token);

      if (result.valid && result.user) {
        recordToken(token, expiresIn);
        storeUser(result.user);
        setOauthUser(result.user);
        setHasHteAccess(result.hasHteAccess !== false); // undefined (dormant) → allowed
        setIsOAuthValidated(true);
        setIsOAuthLoading(false);
        return true;
      } else {
        dropToken();
        clearToken();
        setOauthUser(null);
        setIsOAuthValidated(false);
        setOauthError(result.error || 'Token validation failed');
        setIsOAuthLoading(false);
        return false;
      }
    },
    [recordToken, dropToken]
  );

  /**
   * Start OAuth flow by redirecting to auth service
   */
  const startOAuth = useCallback(() => {
    setIsOAuthLoading(true);
    initiateOAuth();
  }, []);

  /**
   * Clear OAuth state and restart the flow
   */
  const clearOAuthAndRestart = useCallback(() => {
    dropToken();
    clearToken();
    setOauthUser(null);
    setIsOAuthValidated(false);
    setOauthError(null);
    initiateOAuth();
  }, [dropToken]);

  /**
   * Sign out fully — invalidate the refresh cookie + access token at the
   * source, then drop all client-side state.
   */
  const logout = useCallback(async (): Promise<void> => {
    const token = accessTokenRef.current;
    try {
      await oauthServiceLogout(token);
    } finally {
      dropToken();
      clearToken();
      setOauthUser(null);
      setIsOAuthValidated(false);
      setOauthError(null);
    }
  }, [dropToken]);

  /**
   * Refresh-on-load.
   * Try to exchange the HttpOnly refresh cookie for a new access token. On
   * success we render immediately (using the cached user for display). A
   * definitive 401 means the user genuinely has no valid session — let the
   * gate auto-redirect to OAuth. Transient errors leave the cached user
   * visible so a brief outage doesn't force re-auth.
   */
  useEffect(() => {
    const initOAuth = async () => {
      if (!OAUTH_ENABLED) {
        setIsOAuthValidated(true);
        setIsOAuthLoading(false);
        return;
      }

      // Defensive: wipe legacy storage keys from prior versions of the client.
      clearToken();

      const cachedUser = getStoredUser();
      const result = await refreshAccessToken();

      if (result.ok && result.accessToken) {
        recordToken(result.accessToken, result.expiresIn);
        if (cachedUser) {
          setOauthUser(cachedUser);
        }
        // WCO-2914: on the refresh-cookie path the token comes back without an access
        // verdict, so when enforcing we resolve it once here (skipped when dormant to
        // avoid an extra round-trip). The gateway re-checks every request regardless.
        if (HTE_ACCESS_ENFORCE) {
          const verdict = await validateToken(result.accessToken);
          setHasHteAccess(verdict.hasHteAccess !== false);
        }
        setIsOAuthValidated(true);
      } else if (result.transient) {
        // Couldn't reach wfauth. Stay online with the cached user; the
        // scheduler will retry. If we have nothing to display, surface a
        // non-destructive error so the user gets a retry option.
        if (cachedUser) {
          setOauthUser(cachedUser);
          setIsOAuthValidated(true);
        } else {
          setOauthError(result.error || 'Unable to verify session');
        }
      }
      // else: definitive 401 — leave isOAuthValidated=false, no error.
      // OAuthGate will auto-redirect to /auth/google.

      setIsOAuthLoading(false);
    };

    initOAuth();
  }, [recordToken]);

  /**
   * Expiry-aware silent refresh scheduler.
   * Fires `TOKEN_VALIDATION_LEAD_MS` before the access token expires, swaps
   * in a new one via /auth/token/refresh, and reschedules off the new expiry.
   * Definitive 401 ends the session cleanly with a "sessionExpired" error
   * (less disruptive than a hard redirect mid-task). Transient errors
   * leave the token in place and try again.
   */
  useEffect(() => {
    if (!OAUTH_ENABLED || !isOAuthValidated) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const nextDelayMs = (): number => {
      const expiresAt = expiresAtRef.current;
      if (expiresAt) {
        const target = expiresAt - Date.now() - TOKEN_VALIDATION_LEAD_MS;
        return Math.min(
          TOKEN_VALIDATION_INTERVAL_MS,
          Math.max(MIN_VALIDATION_DELAY_MS, target)
        );
      }
      return TOKEN_VALIDATION_FALLBACK_MS;
    };

    const runTick = async () => {
      if (cancelled) return;
      const result = await refreshAccessToken();
      if (cancelled) return;

      if (result.ok && result.accessToken) {
        recordToken(result.accessToken, result.expiresIn);
        // recordToken bumps tokenEpoch → the effect re-runs and reschedules
        // against the fresh expiry. Nothing else to do here.
        return;
      }

      if (!result.transient) {
        // Definitive 401 — refresh cookie is gone or expired.
        dropToken();
        clearToken();
        setOauthUser(null);
        setIsOAuthValidated(false);
        setOauthError('sessionExpired');
        return;
      }

      // Transient — keep the current token, try again later.
      scheduleNext();
    };

    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = setTimeout(runTick, nextDelayMs());
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
    // tokenEpoch is intentional: every successful refresh restarts the timer
    // so the next tick uses the new expiry.
  }, [isOAuthValidated, tokenEpoch, recordToken, dropToken]);

  const value: OAuthContextType = {
    isOAuthValidated,
    isOAuthLoading,
    oauthUser,
    oauthError,
    hasHteAccess,
    startOAuth,
    clearOAuthAndRestart,
    validateAndStoreToken,
    logout,
  };

  return <OAuthContext.Provider value={value}>{children}</OAuthContext.Provider>;
};
